/**
 * Videowall configurável (1 a 12 telas) em vários monitores — Nexora Grid (frontend).
 *
 * Criado em: 16/10/2026 · Última modificação: 02/11/2026 · Versão: 2.1.0
 *
 * P9 do documento 13: a engenharia escolhe quantas telas (1 a 12) e o que cada uma mostra; cada tela é uma página
 * da própria aplicação em modo embutido (sem barra). v2.0: cada tela pode ser **levada para outra tela** — vira uma
 * janela própria, que se arrasta para qualquer monitor; a posição fica guardada e ela reabre no mesmo lugar. Com
 * "Detectar monitores" (Window Management API do Chrome/Edge) escolhe-se o monitor de cada tela e "Abrir nas telas"
 * abre todas de uma vez. A configuração fica neste navegador (o computador do videowall); regras em `regrasVideowall.ts`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { pode } from "../permissoes";
import { useMarca } from "../marca";
import { useSessao } from "../sessao";
import {
  colunasDaGrade, comJanela, conteudoDaTela, lerConfig, limitarTelas, MAX_TELAS, MIN_TELAS, monitorDaPosicao, mudouPosicao, nomeDaJanela,
  nomeDoMonitor, OPCOES_TELA, posicaoNoMonitor, recursosDaJanela, urlDaTela, type Config, type Monitor,
} from "./regrasVideowall";

const CHAVE = "nexora.videowall";
type JanelaComTelas = Window & { getScreenDetails?: () => Promise<{ screens: Monitor[]; addEventListener: (t: string, f: () => void) => void }> };

function ler(): Config {
  try { return lerConfig(localStorage.getItem(CHAVE)); } catch { return lerConfig(null); }
}

export function Videowall() {
  const { usuario } = useSessao();
  const { modo } = useMarca();
  const [config, setConfig] = useState<Config>(ler);
  const [editando, setEditando] = useState(false);
  const [destacadas, setDestacadas] = useState<number[]>([]);
  const [monitores, setMonitores] = useState<Monitor[]>([]);
  const [aviso, setAviso] = useState("");
  const janelas = useRef(new Map<number, Window>());
  const podeConfigurar = !!usuario && pode.configurarVideowall(usuario);
  const temMultiMonitor = typeof window !== "undefined" && "getScreenDetails" in window;

  const salvar = useCallback((mudar: (c: Config) => Config) => {
    setConfig((atual) => {
      const c = mudar(atual);
      try { localStorage.setItem(CHAVE, JSON.stringify(c)); } catch { /* só nesta sessão */ }
      return c;
    });
  }, []);

  // acompanha as janelas soltas: guarda onde a pessoa deixou cada uma e devolve à grade a que foi fechada
  useEffect(() => {
    const id = window.setInterval(() => {
      const fechadas: number[] = [];
      janelas.current.forEach((j, i) => {
        if (j.closed) { fechadas.push(i); return; }
        try {
          const p = { left: j.screenX, top: j.screenY, width: j.outerWidth, height: j.outerHeight };
          salvar((c) => (mudouPosicao(c.janelas[i] ?? null, p) ? comJanela(c, i, p) : c));
        } catch { /* janela em outra origem: mantém a posição anterior */ }
      });
      if (fechadas.length) {
        fechadas.forEach((i) => janelas.current.delete(i));
        setDestacadas((d) => d.filter((i) => !fechadas.includes(i)));
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [salvar]);

  const levar = (i: number, cfg: Config = config): boolean => {
    const j = window.open(urlDaTela(conteudoDaTela(cfg, i), i, true, modo), nomeDaJanela(i), recursosDaJanela(cfg.janelas[i] ?? null));
    if (!j) { setAviso("O navegador bloqueou a janela. Permita janelas pop-up para este endereço e tente de novo."); return false; }
    janelas.current.set(i, j);
    setDestacadas((d) => (d.includes(i) ? d : [...d, i]));
    setAviso("");
    return true;
  };
  const trazer = (i: number) => {
    janelas.current.get(i)?.close();
    janelas.current.delete(i);
    setDestacadas((d) => d.filter((k) => k !== i));
  };
  const abrirTodas = () => {
    for (let i = 0; i < config.telas; i++) if (!levar(i)) break;
  };
  const telaCheia = (i: number) => {
    void document.getElementById(`vw-tela-${i}`)?.requestFullscreen?.().catch(() => undefined);
  };

  const detectarMonitores = async () => {
    const w = window as JanelaComTelas;
    if (!w.getScreenDetails) return;
    try {
      const detalhes = await w.getScreenDetails();
      const atualizar = () => setMonitores(detalhes.screens.map((s) => ({ availLeft: s.availLeft, availTop: s.availTop, availWidth: s.availWidth,
        availHeight: s.availHeight, label: s.label, isPrimary: s.isPrimary })));
      atualizar();
      detalhes.addEventListener("screenschange", atualizar);
      setAviso("");
    } catch {
      setAviso("Sem permissão para ver os monitores. Libere \"Gerenciamento de janelas\" nas permissões do site; dá para arrastar cada janela mesmo assim.");
    }
  };
  const escolherMonitor = (i: number, indice: number) => {
    const m = monitores[indice];
    if (!m) return;
    const p = posicaoNoMonitor(m);
    salvar((c) => comJanela(c, i, p));
    const j = janelas.current.get(i);
    if (j && !j.closed) { try { j.moveTo(p.left, p.top); j.resizeTo(p.width, p.height); } catch { /* o navegador recusou mover */ } }
  };

  const naGrade = Array.from({ length: config.telas }, (_, i) => i).filter((i) => !destacadas.includes(i));
  const colunas = colunasDaGrade(naGrade.length);
  return (
    <section className="videowall">
      <header className="linha-titulo">
        <h2 className="nx-titulo">Videowall · {config.telas} {config.telas === 1 ? "tela" : "telas"}</h2>
        <span className="espaco" />
        {temMultiMonitor && <button type="button" className="nx-botao" onClick={detectarMonitores}>
          {monitores.length ? `${monitores.length} monitores` : "Detectar monitores"}</button>}
        <button type="button" className="nx-botao" onClick={abrirTodas}>Abrir nas telas</button>
        {destacadas.length > 0 && <button type="button" className="nx-botao" onClick={() => [...destacadas].forEach(trazer)}>Trazer todas de volta</button>}
        {podeConfigurar && <button type="button" className="nx-botao" onClick={() => setEditando(!editando)}>
          {editando ? "Concluir" : "Configurar"}</button>}
      </header>
      {aviso && <p className="nx-aviso" role="status">{aviso}</p>}
      {editando && (
        <div className="nx-cartao config-videowall">
          <div className="nx-campo">
            <label htmlFor="telas">Quantidade de telas ({MIN_TELAS} a {MAX_TELAS})</label>
            <input id="telas" type="number" min={MIN_TELAS} max={MAX_TELAS} value={config.telas}
              onChange={(e) => salvar((c) => ({ ...c, telas: limitarTelas(Number(e.target.value)) }))} />
          </div>
          {Array.from({ length: config.telas }, (_, i) => (
            <div key={i} className="nx-campo">
              <label htmlFor={`tela-${i}`}>Tela {i + 1}</label>
              <select id={`tela-${i}`} value={conteudoDaTela(config, i)}
                onChange={(e) => { const v = e.target.value; salvar((c) => { const cs = [...c.conteudos]; cs[i] = v; return { ...c, conteudos: cs }; }); }}>
                {OPCOES_TELA.map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
              </select>
              {monitores.length > 0 && (
                <select aria-label={`Monitor da tela ${i + 1}`} value={monitorDaPosicao(config.janelas[i] ?? null, monitores)}
                  onChange={(e) => escolherMonitor(i, Number(e.target.value))}>
                  <option value={-1}>Onde eu arrastar</option>
                  {monitores.map((m, k) => <option key={k} value={k}>{nomeDoMonitor(m, k)}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="grade-videowall" style={{ gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` }}>
        {naGrade.map((i) => (
          <figure key={`${i}-${conteudoDaTela(config, i)}`} className="tela-videowall">
            <figcaption>
              <span>Tela {i + 1} · {OPCOES_TELA.find((o) => o.valor === conteudoDaTela(config, i))?.texto}</span>
              <button type="button" onClick={() => levar(i)} title="Abre numa janela própria; arraste-a para o monitor que quiser">Levar para outra tela</button>
              <button type="button" onClick={() => telaCheia(i)}>Tela cheia</button>
            </figcaption>
            <iframe id={`vw-tela-${i}`} title={`Tela ${i + 1}`} src={urlDaTela(conteudoDaTela(config, i), i, false, modo)} allowFullScreen />
          </figure>
        ))}
        {naGrade.length === 0 && <p className="vw-vazia">Todas as telas estão em janelas próprias.</p>}
      </div>
      {destacadas.length > 0 && (
        <ul className="vw-destacadas" aria-label="Telas em janelas próprias">
          {[...destacadas].sort((a, b) => a - b).map((i) => (
            <li key={i}>
              <span>Tela {i + 1} em janela própria{monitores.length > 0 && monitorDaPosicao(config.janelas[i] ?? null, monitores) >= 0
                ? ` · ${nomeDoMonitor(monitores[monitorDaPosicao(config.janelas[i] ?? null, monitores)], monitorDaPosicao(config.janelas[i] ?? null, monitores))}` : ""}</span>
              <button type="button" onClick={() => janelas.current.get(i)?.focus()}>Mostrar</button>
              <button type="button" onClick={() => trazer(i)}>Trazer de volta</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
